import React from 'react';

import { AnswerTypes } from 'utils/api/modules/incidents/incidents.types';

import { ProtectorType } from 'core/constants';

import ClampedText from 'components/ClampedText';

import { DisplayLabel, DisplayList, DisplayRow, EntryCard, EntryTitle, NoEntries } from './RepeatingGroupField.styles';
import { RepeatingProperties } from './RepeatingGroupField';

type Entry = Record<string, AnswerTypes>;

interface RepeatingGroupDisplayProps {
  properties: RepeatingProperties;
  entries: Entry[];
}

// Read-only display of a procedures answer. Used on two surfaces with different value shapes:
//   - the Review-and-Submit step (FormPreview), where values are the draft's catalogue *keys*
//     (choice sub-fields are arrays like ['cat-x']) — resolved to labels here via the sub-field enum;
//   - the Original Submission record tab, where the backend has already resolved values to label
//     strings — passed straight through.
// resolve() handles both. Comments (long text) clamp with a "View more" toggle.
const RepeatingGroupDisplay = ({ properties, entries }: RepeatingGroupDisplayProps): React.ReactElement => {
  const { order, properties: subDefs, nounTitle, nounPlural } = properties;

  if (!entries || entries.length === 0) {
    return <NoEntries>No {nounPlural} recorded</NoEntries>;
  }

  const resolve = (subKey: string, raw: AnswerTypes): string => {
    const options = subDefs[subKey]?.properties?.enum;
    if (options && Array.isArray(raw)) {
      return (
        raw
          .map((key) => options.find((o) => o.key === key)?.value ?? '')
          .filter(Boolean)
          .join(', ') || '—'
      );
    }
    return raw === undefined || raw === null || raw === '' ? '—' : String(raw);
  };

  return (
    <DisplayList>
      {entries.map((entry, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <EntryCard key={idx}>
          <EntryTitle>
            {nounTitle} {idx + 1}
          </EntryTitle>
          {order.map((subKey) => {
            const raw = entry[subKey];
            const isLongText = subDefs[subKey]?.questionType === ProtectorType.LongText;
            const text = resolve(subKey, raw);
            return (
              <DisplayRow key={subKey}>
                <DisplayLabel>{subDefs[subKey]?.description}</DisplayLabel>
                {isLongText && typeof raw === 'string' && raw !== '' ? (
                  <ClampedText>{raw}</ClampedText>
                ) : (
                  <span>{text}</span>
                )}
              </DisplayRow>
            );
          })}
        </EntryCard>
      ))}
    </DisplayList>
  );
};

export default RepeatingGroupDisplay;
